import React from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/contexts/ThemeContext';

const REGIONS = [
    {
        continent: 'Americas',
        countries: [
            { code: 'US', name: 'United States', currency: 'USD' },
            { code: 'CA', name: 'Canada', currency: 'CAD' },
            { code: 'BR', name: 'Brazil', currency: 'BRL' },
            { code: 'MX', name: 'Mexico', currency: 'MXN' }
        ]
    },
    {
        continent: 'Europe, Middle East and Africa',
        countries: [
            { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
            { code: 'EU', name: 'European Union', currency: 'EUR' },
            { code: 'AE', name: 'UAE', currency: 'AED' },
            { code: 'DE', name: 'Germany', currency: 'EUR' },
            { code: 'FR', name: 'France', currency: 'EUR' },
            { code: 'ZA', name: 'South Africa', currency: 'ZAR' }
        ]
    },
    {
        continent: 'Asia Pacific',
        countries: [
            { code: 'IN', name: 'India', currency: 'INR' },
            { code: 'JP', name: 'Japan', currency: 'JPY' },
            { code: 'SG', name: 'Singapore', currency: 'SGD' },
            { code: 'AU', name: 'Australia', currency: 'AUD' },
            { code: 'NP', name: 'Nepal', currency: 'NPR' },
            { code: 'CN', name: 'China', currency: 'CNY' }
        ]
    }
];

export default function CountrySelect({ selected, onSelect, muted = false }) {
    const { theme } = useTheme();
    const allCountries = REGIONS.flatMap(r => r.countries);
    const current = allCountries.find(c => c.code === selected) || allCountries[0];

    return (
        <div className="group relative notranslate">
            {/* Trigger with World Icon */}
            <div 
                style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px', 
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s',
                    opacity: muted ? 0.8 : 1
                }} 
                className="hover:text-[var(--text-primary)] transition-all hover:opacity-100"
            >
                <ObliqueIcon name="public" size={13} style={{ opacity: muted ? 0.6 : 1 }} />
                <span style={{ fontSize: '12px', fontWeight: '600', color: muted ? 'var(--text-tertiary)' : 'inherit' }}>{current.code} ({current.currency})</span>
            </div>
            
            {/* Mega Menu Style Dropdown */}
            <div 
                className="absolute top-full right-0 mt-4 w-[720px] border border-[var(--border)] rounded-2xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100] overflow-hidden"
                style={{ 
                    background: theme === 'dark' ? '#161b2e' : '#ffffff', 
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }}
            >
                <div style={{ padding: '32px 40px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr 1.5fr', gap: '40px' }}>
                        {REGIONS.map(reg => (
                            <div key={reg.continent}>
                                <h4 style={{ fontSize: '12px', fontWeight: '800', color: 'var(--text-primary)', marginBottom: '14px', borderBottom: '1px solid var(--border)', paddingBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                    {reg.continent}
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    {reg.countries.map(c => (
                                        <div 
                                            key={c.code} 
                                            onClick={() => onSelect(c.code)}
                                            style={{ 
                                                padding: '6px 12px', 
                                                borderRadius: '8px', 
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                background: selected === c.code ? 'var(--bg-page)' : 'transparent',
                                                color: selected === c.code ? 'var(--accent)' : 'var(--text-secondary)',
                                                transition: 'all 0.15s',
                                                whiteSpace: 'nowrap'
                                            }}
                                            className="hover:bg-[var(--bg-page)] hover:text-[var(--text-primary)] group/item"
                                        >
                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--bg-muted)] text-[var(--text-secondary)] border border-[var(--border)] tracking-wider">
                                                {c.code}
                                            </span>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: '13px', fontWeight: '600' }}>{c.name}</div>
                                            </div>
                                            {selected === c.code && <ObliqueIcon name="check" size={12} />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Footer / Extra Info */}
                <div style={{ padding: '16px 40px', background: 'var(--bg-page)', borderTop: '1px solid var(--border)', fontSize: '11px', color: 'var(--text-tertiary)', fontWeight: '500' }}>
                    Selecting a region changes the pricing and available features.
                </div>
            </div>
        </div>
    );
}
